import { useState, useCallback } from 'react'
import axios from 'Utils/axios'
import { ProductInfo, SearchProgress } from '../types'
import { splitIntoBatches, createFallbackProductInfo } from '../utils'
import { BATCH_SIZE, MAX_SEARCH_COUNT, BATCH_DELAY } from '../constants'
import productImagePlaceholder from 'Assets/images/product_img_placeholder.png'

interface UseProductSearchProps {
  segmentType?: string
  updateProductInfo: (productValue: string, productData: ProductInfo) => void
}

export const useProductSearch = ({ segmentType, updateProductInfo }: UseProductSearchProps) => {
  const [searchingMissingProducts, setSearchingMissingProducts] = useState(false)
  const [searchProgress, setSearchProgress] = useState<SearchProgress | null>(null)
  const [searchedProductIds, setSearchedProductIds] = useState<Set<string>>(new Set())

  const searchMissingProductsForViewMode = useCallback(async (missingProductIds: string[]) => {
    console.log('🔍 searchMissingProductsForViewMode called with:', missingProductIds.length, 'products')
    
    // Filter out products we've already searched for
    const newProductsToSearch = missingProductIds.filter(id => !searchedProductIds.has(id))
    
    if (newProductsToSearch.length === 0) {
      console.log('❌ Search cancelled - all products already searched or no missing products')
      return
    }

    console.log(`🔍 Filtered search: ${newProductsToSearch.length}/${missingProductIds.length} new products to search`)

    // Mark these products as being searched
    setSearchedProductIds(prev => {
      const newSet = new Set(prev)
      newProductsToSearch.forEach(id => newSet.add(id))
      return newSet
    })

    // Limit search count to avoid overwhelming the system
    const productsToSearch = newProductsToSearch.slice(0, MAX_SEARCH_COUNT)
    const remainingProducts = newProductsToSearch.slice(MAX_SEARCH_COUNT)
    
    if (remainingProducts.length > 0) {
      console.log(`⚡ Performance optimization: searching first ${MAX_SEARCH_COUNT} products, creating fallbacks for remaining ${remainingProducts.length}`)
      
      // Create immediate fallback entries for products beyond the search limit
      remainingProducts.forEach(id => {
        const fallbackInfo = createFallbackProductInfo(id)
        updateProductInfo(id, fallbackInfo)
      })
    }

    console.log('⏳ Starting optimized batch search for', productsToSearch.length, 'products...')
    setSearchingMissingProducts(true)
    
    try {
      // Split into batches for better performance
      const batches = splitIntoBatches(productsToSearch, BATCH_SIZE)
      
      console.log(`📦 Processing ${batches.length} batches of max ${BATCH_SIZE} products each`)
      
      let totalFoundCount = 0
      setSearchProgress({ current: 0, total: batches.length })
      
      // Process batches sequentially with small delays to avoid blocking UI
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        try {
          const batch = batches[batchIndex]
          setSearchProgress({ current: batchIndex + 1, total: batches.length })
          
          const searchQuery = batch.join(' ')
          
          console.log(`📡 Batch ${batchIndex + 1}/${batches.length}: searching ${batch.length} products`)
          
          const response = await axios.get('/audience-dimensions/dimensions', {
            params: {
              dimension_code: segmentType,
              keyword: searchQuery,
              per_page: 50,
              page: 1,
            },
          })
          
          const searchResults = response.data.data.map((op: any) => ({ 
            label: op?.name || op?.label, 
            value: op?.code,
            image: op?.image || productImagePlaceholder,
            ...op 
          }))
          
          console.log(`📦 Batch ${batchIndex + 1} results:`, searchResults.length, 'products found')
          
          // Update product info with found results
          let batchFoundCount = 0
          searchResults.forEach((result: any) => {
            const matchingId = batch.find(id => 
              result.value === id ||
              (result as any).code === id
            )
            
            if (matchingId) {
              const properProductInfo = {
                ...result,
                label: result.label || result.name || matchingId,
                value: matchingId,
                image: result.image || productImagePlaceholder
              }
              
              updateProductInfo(matchingId, properProductInfo)
              batchFoundCount++
            }
          })
          
          // Create fallback for products not found in this batch
          const notFoundInBatch = batch.filter(id => 
            !searchResults.some((result: any) => 
              result.value === id || (result as any).code === id
            )
          )
          
          notFoundInBatch.forEach(id => {
            const fallbackInfo = createFallbackProductInfo(id)
            updateProductInfo(id, fallbackInfo)
          })
          
          totalFoundCount += batchFoundCount
          console.log(`✅ Batch ${batchIndex + 1}: found ${batchFoundCount}/${batch.length}, total so far: ${totalFoundCount}`)
          
          // Small delay between batches to prevent UI blocking
          if (batchIndex < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, BATCH_DELAY))
          }
          
        } catch (error) {
          console.error(`❌ Error in batch ${batchIndex + 1}:`, error)
          
          // Create fallback entries for this batch on error
          const batch = batches[batchIndex]
          batch.forEach(id => {
            const fallbackInfo = createFallbackProductInfo(id)
            updateProductInfo(id, fallbackInfo)
          })
        }
      }
      
      setSearchProgress(null)
      console.log(`🎉 Sequential batch search completed: ${totalFoundCount}/${productsToSearch.length} products found from API`)
      
    } catch (error) {
      console.error('❌ Critical error in batch search:', error)
      
      // Create fallback entries for all missing products on critical error
      productsToSearch.forEach(id => {
        const fallbackInfo = createFallbackProductInfo(id)
        updateProductInfo(id, fallbackInfo)
      })
    } finally {
      console.log('⏹️ Optimized view mode search completed')
      setSearchingMissingProducts(false)
    }
  }, [updateProductInfo, segmentType, searchedProductIds])

  return {
    searchingMissingProducts,
    searchProgress,
    searchedProductIds,
    searchMissingProductsForViewMode,
    setSearchedProductIds
  }
} 